import {
  Button, Card, ErrorState, Grid, InvitationIssuedPanel, LoadingState, LogoPicker, Notice, PageHeader, Permissions,
  Row, Stack, Stat, StatusBadge, Textarea, WEEKDAYS, usePermission, useApi, useI18n,
  type TenantProfileResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Detail } from '../components/Detail';
import { ModulesCard } from '../components/ModulesCard';
import { SubscriptionCard } from '../components/SubscriptionCard';
import { TenantEditCard } from '../components/TenantEditCard';

/**
 * One tenant's operational profile (FR-PLT-04) and its lifecycle controls (FR-PLT-05).
 *
 * What is *not* here matters as much as what is: no child's name, photograph, report or message. The
 * endpoint does not return them and this screen has no route to them. A platform employee who needs to
 * see inside a tenant goes through a support session instead, which the tenant approves and which is
 * time-boxed and logged (SRS §3.5).
 */
export function TenantDetail() {
  const { id } = useParams();
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatDate, formatNumber, formatPhone } = useI18n();
  const canSuspend = usePermission(Permissions.platform.suspendTenants);
  const canUpdate = usePermission(Permissions.platform.updateTenants);
  const canManageModules = usePermission(Permissions.platform.manageModules);

  const tenantId = Number(id);

  const tenant = useQuery({
    queryKey: ['platform', 'tenant', tenantId],
    queryFn: () => api.platform.getTenant(tenantId),
    enabled: Number.isFinite(tenantId),
  });

  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Every change answers with the whole profile. The list and the dashboard carry the status, names and trials
  // too, and are stale everywhere after one.
  const changed = (updated: TenantProfileResponse) => {
    queryClient.setQueryData(['platform', 'tenant', tenantId], updated);
    void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['platform', 'dashboard'] });
  };

  const lifecycle = useMutation({
    mutationFn: (action: 'suspend' | 'reactivate') =>
      action === 'suspend'
        ? api.platform.suspendTenant(tenantId, reason)
        : api.platform.reactivateTenant(tenantId),
    onSuccess: (updated) => {
      changed(updated);
      setConfirming(false);
      setReason('');
    },
  });

  // The profile carries the logo, so both it and the list (which shows it too) are stale after a change.
  const logoChanged = (logoUrl: string | null) => {
    queryClient.setQueryData(['platform', 'tenant', tenantId], (current: typeof tenant.data) =>
      current ? { ...current, logoUrl } : current);
    void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
  };

  // A new link for an owner who has not accepted yet; refused once they have (decision 2026-09-23 #16).
  const resendOwner = useMutation({ mutationFn: () => api.platform.resendOwnerInvitation(tenantId) });

  if (tenant.isPending) return <LoadingState />;
  if (tenant.isError) return <ErrorState error={tenant.error} onRetry={() => void tenant.refetch()} />;

  const data = tenant.data;
  const suspended = data.status === 'Suspended';
  const weekend = WEEKDAYS.filter((day) => data.weekendDays.includes(day)).map((day) => t(`weekday.${day}`));

  return (
    <>
      <PageHeader
        title={data.nameAr}
        lede={data.nameEn ?? undefined}
        actions={<Link className="dg-button dg-button--ghost" to="/tenants">{t('action.back')}</Link>}
      />

      <Row>
        <StatusBadge status={data.status} />
        <span>{t('tenant.entryPath')} <code dir="ltr">/{data.slug}</code></span>
      </Row>

      <Notice tone="info" title={t('tenant.profile')}>{t('tenant.privacyNote')}</Notice>

      <Grid>
        <Stat label={t('tenants.column.children')} value={formatNumber(data.activeChildren)} />
        <Stat label={t('tenants.column.staff')} value={formatNumber(data.activeStaff)} />
        <Stat label={t('tenants.column.classes')} value={formatNumber(data.activeClasses)} />
      </Grid>

      <Card title={t('tenant.contact')}>
        <dl className="dg-grid" style={{ margin: 0 }}>
          <Detail label={t('tenant.type')} value={t(`tenants.new.type.${data.type}`)} />
          <Detail label={t('tenant.country')} value={data.country ?? '—'} />
          <Detail label={t('tenant.timeZone')} value={data.timeZone} />
          <Detail label={t('tenant.currency')} value={data.currency} />
          <Detail label={t('tenants.new.contactPhone')} value={formatPhone(data.contactPhone)} />
          <Detail label={t('tenants.new.contactEmail')} value={data.contactEmail ?? '—'} />
          <Detail label={t('tenant.address')} value={data.address ?? '—'} />
          <Detail label={t('identity.weekendDays')} value={weekend.length > 0 ? weekend.join('، ') : '—'} />
          <Detail label={t('tenant.created')} value={formatDate(data.createdAt)} />
        </dl>
      </Card>

      <SubscriptionCard tenant={data} onChanged={changed} />

      {canUpdate && <TenantEditCard tenant={data} onChanged={changed} />}

      {canManageModules && <ModulesCard tenantId={tenantId} />}

      {canUpdate && (
        <Card title={t('logo.title')}>
          <LogoPicker
            name={data.nameAr}
            logoUrl={data.logoUrl}
            hint={t('logo.hint')}
            onSave={async (logo) => logoChanged((await api.platform.setTenantLogo(tenantId, logo)).logoUrl)}
            onRemove={async () => { await api.platform.removeTenantLogo(tenantId); logoChanged(null); }}
          />
        </Card>
      )}

      {canUpdate && (
        <Card title={t('tenant.ownerInvitation')} hint={t('tenant.ownerInvitation.hint')}>
          <Stack>
            {resendOwner.isError && <ErrorState error={resendOwner.error} />}
            {resendOwner.data && (
              <InvitationIssuedPanel invitation={resendOwner.data} onDismiss={() => resendOwner.reset()} />
            )}
            <Row>
              <Button
                variant="secondary"
                busy={resendOwner.isPending}
                busyLabel={t('action.saving')}
                onClick={() => resendOwner.mutate()}
              >
                {t('invitation.resend')}
              </Button>
            </Row>
          </Stack>
        </Card>
      )}

      {canSuspend && (
        <Card title={suspended ? t('tenant.reactivate') : t('tenant.suspend')}>
          <Stack>
            {lifecycle.isError && <ErrorState error={lifecycle.error} />}

            {suspended && <p className="dg-card__hint">{t('tenant.reactivate.hint')}</p>}
            {suspended ? (
              <Row>
                <Button
                  onClick={() => lifecycle.mutate('reactivate')}
                  busy={lifecycle.isPending}
                  busyLabel={t('action.saving')}
                >
                  {t('tenant.reactivate')}
                </Button>
              </Row>
            ) : confirming ? (
              <>
                {/* Suspension cuts off every user at the nursery at once, so it asks for a reason before
                    it will proceed — the reason is written to the audit log and shown to the owner. */}
                <Notice tone="warning">{t('tenant.suspend.hint')}</Notice>
                <label className="dg-field">
                  <span className="dg-field__label">{t('tenant.suspend.reason')}</span>
                  <Textarea
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    required
                    autoFocus
                  />
                </label>
                <Row>
                  <Button
                    variant="danger"
                    disabled={reason.trim().length === 0}
                    busy={lifecycle.isPending}
                    busyLabel={t('action.saving')}
                    onClick={() => lifecycle.mutate('suspend')}
                  >
                    {t('tenant.suspend.confirm')}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirming(false)}>{t('action.cancel')}</Button>
                </Row>
              </>
            ) : (
              <Row>
                <Button variant="danger" onClick={() => setConfirming(true)}>{t('tenant.suspend')}</Button>
              </Row>
            )}
          </Stack>
        </Card>
      )}
    </>
  );
}
